<template>
  <!--
    errors-14 / mobile-11 / glitch-14:
    - The live region is always in the page and only its content comes and goes: a status region inserted together
      with its text is not reliably announced, so screen-reader users never heard about an update.
    - Bottom-left at every width, clear of the home indicator and a landscape notch. It used to sit at the top on
      phones, under the installed app's status bar where it could not be tapped. The install card (PwaInstallCard.vue)
      shares the corner and waits while this prompt is up (composables/pwa-update.ts).
    - z-50, below the mobile nav drawer (z-60) and PrimeVue's Drawer and Toast: it painted over the open nav drawer.
    - "Update now" reloads the page, so it says so, and warns when the page holds something a reload throws away
      (utils/reload-blockers.ts: a playing video, unsaved edits). That reload is the member's own, so it is not held.
  -->
  <div role="status" aria-live="polite">
    <div
        v-if="needRefresh && !appStore.isAuthenticating"
        class="toast toast-start toast-bottom z-50 start-[max(1rem,env(safe-area-inset-left,0px))] bottom-[calc(env(safe-area-inset-bottom,0px)_+_1rem)]"
    >
      <div class="alert alert-info alert-vertical sm:alert-horizontal shadow-lg gap-2 max-w-md">
        <p class="text-pretty">{{ message }}</p>
        <div class="flex gap-2">
          <button type="button" class="btn btn-sm btn-ghost" @click="dismiss">Later</button>
          <button type="button" class="btn btn-sm btn-primary" @click="handleUpdate">Update now</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useIntervalFn } from '@vueuse/core';
import { FiveMinutesInMilliseconds } from '@/constants';
import { useApplicationStore } from '@/stores/Application';
import { ResumeCatchUpAfterMilliseconds, useAppResume } from '@/composables/useAppResume';
import { createReloadGuard } from '@/utils/pwa-reload-guard';
import { isReloadBlocked } from '@/utils/reload-blockers';
import { serviceWorkerRegistration, usePwaUpdate } from '@/composables/pwa-update';

// tmi-6gh T3: a reload in the middle of a sign-in / token handoff (while user + permissions are being
// fetched) can leave the user authenticated-but-freemium. Two paths could cause one, and both wait:
//  - this tab's "Update now": the banner is hidden while `isAuthenticating`, so it cannot be clicked;
//  - a `controllerchange` caused by ANOTHER tab's "Update now" (tmi-udo reloads every client): the reload
//    guard defers it until `isAuthenticating` turns false, then reloads once (F-I3).
const appStore = useApplicationStore();

// Registers the service worker. `needRefresh` is shared: the install card waits while this prompt is up.
const { needRefresh, updateServiceWorker } = usePwaUpdate();

// lifecycle-11: the update poll sleeps while the tab is hidden and catches up, like the sync loop, when the
// member returns or reconnects after more than a minute.
let lastUpdateCheckAt = 0;

async function checkForUpdate(): Promise< void > {
    const registration = serviceWorkerRegistration();
    if ( !registration || registration.installing || 'hidden' === visibility.value ) return;
    if ( 'connection' in navigator && !navigator.onLine ) return;

    lastUpdateCheckAt = Date.now();
    try {
        await registration.update();
    } catch {
        // Offline or the server hiccuped: the next poll or catch-up asks again.
    }
}

const visibility = useAppResume( () => {
    if ( Date.now() - lastUpdateCheckAt >= ResumeCatchUpAfterMilliseconds ) {
        void checkForUpdate();
    }
} );
useIntervalFn( checkForUpdate, FiveMinutesInMilliseconds );

const reloadGuard = createReloadGuard( {
    hadController: 'serviceWorker' in navigator && null !== navigator.serviceWorker.controller,
    reload: () => window.location.reload(),
    isBusy: () => appStore.isAuthenticating,
    // Unsaved edits, a playing video: features register these in utils/reload-blockers.ts.
    isBlocked: isReloadBlocked,
    // One-shot: stops itself the first time nothing defers the reload any more, then performs it.
    onIdle: ( resume, isDeferred ) => {
        const stop = watch( isDeferred, deferred => {
            if ( deferred ) return;
            stop();
            resume();
        } );
    }
} );
if ( 'serviceWorker' in navigator ) {
    navigator.serviceWorker.addEventListener( 'controllerchange', () => reloadGuard.onControllerChange() );
}

// Reactive: the registry and the blockers' own refs are tracked, so the warning follows a video starting or stopping.
const message = computed( () => isReloadBlocked()
    ? 'A new version is available. Updating reloads this page and interrupts what is in progress here.'
    : 'A new version is available. Updating reloads this page.' );

function dismiss() {
    needRefresh.value = false;
}

async function handleUpdate() {
    reloadGuard.userRequestedUpdate();
    await updateServiceWorker( true );
}
</script>
